import { useSortable } from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import {
  Accordion,
  ActionIcon,
  Button,
  Group,
  Input,
  Modal,
  Paper,
  Select,
  SegmentedControl,
  Stack,
  Text,
  ThemeIcon,
  Tooltip,
} from "@mantine/core"
import { UseFormReturnType } from "@mantine/form"
import { useDisclosure, useUncontrolled } from "@mantine/hooks"
import {
  IconCloudSearch,
  IconExclamationCircle,
  IconGripVertical,
} from "@tabler/icons-react"
import { useFeatureFlag } from "configcat-react"
import _ from "lodash"
import { memo, useEffect, useMemo, useState } from "react"

import {
  Allocation,
  VirtualDimensionsRequests,
} from "@costory/types/endpoints/virtualDimensions"
import { Prisma } from "@costory/types/prisma-client"

import { formatNumber } from "@costory/shared/utils/format"

import { AxesSelector } from "@costory/front/components/AxesSelector"
import { QueryBuilderWrapper } from "@costory/front/components/QueryBuilderWrapper"
import { SelectCreatable } from "@costory/front/components/SelectCreatable"
import { AutoSplitTable } from "@costory/front/pages/VirtualDimensions/components/AutoSplitTable"
import { RuleEditorAccordionControl } from "@costory/front/pages/VirtualDimensions/components/RuleEditorAccordionControl"
import SplitCostResult from "@costory/front/pages/VirtualDimensions/components/SplitCostResult"
import { ALLOCATION_TYPES } from "@costory/front/pages/VirtualDimensions/constants"
import { showError } from "@costory/front/utils/validation"

type Props = {
  isExpanded: boolean
  onToggleAccordion: () => void
  onDuplicate: () => void
  form: UseFormReturnType<VirtualDimensionsRequests.EditVirtualDimension>
  ruleIndex: number
  id: string
  index: number
  allocatedCost: number | undefined
  exploreCost: () => void
  organizationUsers?: (Prisma.UserGetPayload<Record<string, never>> & {
    fullName: string
  })[]
}

export const getDefaultAllocationConfig = (allocationType: string) => {
  switch (allocationType) {
    case "splitCost":
      return {
        allocationType: "splitCost",
        reAllocationParams: {
          partitions: [],
          type: "custom",
        },
      }
    case "dimensionValue":
      return {
        allocationType: "dimensionValue",
        dimensionValue: null,
      }
    case "existingColumn":
      return {
        allocationType: "existingColumn",
        existingColumn: null,
      }
    case "telemetry":
      return {
        allocationType: "telemetry",
        datasource: "",
        mappingParams: {
          mapping: {},
        },
        mappingType: "identity",
      }
    default:
      return {}
  }
}

export const RuleEditor = ({
  form,
  ruleIndex,
  isExpanded,
  onToggleAccordion,
  onDuplicate,
  id,
  index,
  allocatedCost,
  exploreCost,
  organizationUsers = [],
}: Props) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  const [isOpen, { open, close }] = useDisclosure()
  const [allocationConfigs, setAllocationConfigs] = useState<
    Record<string, Allocation>
  >({})

  const hasError = useMemo(() => {
    const formErrorsKeys = Object.keys(form.errors)
    return formErrorsKeys.some((key) => key.includes(`rules.${ruleIndex}`))
  }, [form.errors, ruleIndex])
  const rule = form.getValues().rules[ruleIndex]
  const allocation = rule.allocation
  const {
    value: dimentionValue,
    defaultValue,
    onChange,
  } = form.getInputProps(`rules.${ruleIndex}.allocation.dimensionValue`)

  const [value, setValue] = useUncontrolled({
    value: dimentionValue,
    defaultValue,
    finalValue: "Other",
    onChange,
  })
  useEffect(() => {
    const defaultConfig = getDefaultAllocationConfig(allocation.allocationType)
    if (!_.isEqual(allocation, defaultConfig)) {
      setAllocationConfigs({})
    }
  }, [allocation])
  const { value: isEnabled } = useFeatureFlag("autosplitcost", false)
  // Internal State issue: allocation type value doesn't update correctly on collapse/uncollapse
  const SegmentedControlComponent = memo(() => (
    <SegmentedControl
      data={ALLOCATION_TYPES.filter(
        ({ value }) => value !== "telemetry" || isEnabled,
      )}
      {...form.getInputProps(`rules.${ruleIndex}.allocation.allocationType`)}
      onChange={(val) => {
        const currentType = allocation.allocationType
        if (currentType && currentType !== val) {
          setAllocationConfigs((prev) => ({
            ...prev,
            [currentType]: { ...allocation },
          }))
        }
        const restoredConfig =
          allocationConfigs[val] || getDefaultAllocationConfig(val)
        form.setFieldValue(`rules.${ruleIndex}.allocation`, restoredConfig)
        form.validate()
      }}
      key={form.key(`rules.${ruleIndex}.allocation.allocationType`)}
      data-intercom-target="costory-tour-vdim-allocation-type"
    />
  ))
  SegmentedControlComponent.displayName = "SegmentedControlComponent"

  const availableDimensionValues = _.uniq(form.getValues().values)

  const ownerSelectItems = useMemo(() => {
    if (!organizationUsers || organizationUsers.length === 0) {
      return []
    }
    return organizationUsers
      .filter((user) => user?.id && user?.fullName)
      .map((user) => ({
        value: String(user.id),
        label: user.fullName,
      }))
  }, [organizationUsers])

  return (
    <Paper
      ref={setNodeRef}
      style={style}
      data-intercom-target="vdim-rule-paper"
    >
      <Group wrap="nowrap" gap={0} justify="stretch">
        <ActionIcon
          {...attributes}
          {...listeners}
          variant="transparent"
          c="textMuted"
          data-intercom-target="costory-tour-vdim-drag-handle"
        >
          <IconGripVertical />
        </ActionIcon>
        <Accordion
          w="98%"
          onChange={onToggleAccordion}
          value={isExpanded ? "open" : "closed"}
          chevronPosition="left"
        >
          <Accordion.Item w="100%" value="open" style={{ border: "none" }}>
            <RuleEditorAccordionControl
              onRename={open}
              onDuplicate={onDuplicate}
              onDelete={() => form.removeListItem("rules", ruleIndex)}
            >
              <Group justify="space-between" align="center">
                <Group gap={4}>
                  {hasError && (
                    <Tooltip
                      label={
                        <>
                          {showError({
                            prefix: `rules.${index}`,
                            errors: form.errors,
                          }).join(" ")}
                        </>
                      }
                    >
                      <ThemeIcon c="red.5">
                        <IconExclamationCircle />
                      </ThemeIcon>
                    </Tooltip>
                  )}

                  <Text size="lg" fw="bold" c={hasError ? "red.5" : "inherit"}>
                    Rule {index}:{" "}
                    {rule.name.length > 0
                      ? rule.name
                      : rule.allocation.allocationType == "dimensionValue"
                        ? rule.allocation.dimensionValue
                        : ""}
                  </Text>
                </Group>
                <Group gap="md" align="center">
                  {allocatedCost ? (
                    <Text>
                      {formatNumber({
                        value: allocatedCost,
                      })}
                    </Text>
                  ) : null}
                  <Select
                    placeholder="Select owner"
                    data={ownerSelectItems}
                    value={form.getValues().rules[ruleIndex].ownerId || null}
                    onChange={(value) => {
                      form.setFieldValue(
                        `rules.${ruleIndex}.ownerId`,
                        value || undefined,
                      )
                    }}
                    error={
                      form.getInputProps(`rules.${ruleIndex}.ownerId`).error !==
                      undefined
                    }
                    w={260}
                    clearable
                  />
                </Group>
              </Group>
            </RuleEditorAccordionControl>
            <Accordion.Panel>
              <Stack>
                <QueryBuilderWrapper
                  {...form.getInputProps(`rules.${ruleIndex}.query`)}
                  resetKey={form.key(`rules.${ruleIndex}.query`)}
                />
                <Group justify="space-between">
                  <Group>
                    <SegmentedControlComponent />
                    {allocation.allocationType == "dimensionValue" && (
                      <SelectCreatable
                        key={form.key(
                          `rules.${ruleIndex}.allocation.dimensionValue`,
                        )}
                        data={availableDimensionValues}
                        {...form.getInputProps(
                          `rules.${ruleIndex}.allocation.dimensionValue`,
                        )}
                        handleCreate={(val) =>
                          form.insertListItem("values", val)
                        }
                        value={value}
                        onChange={setValue}
                        error={
                          form.getInputProps(
                            `rules.${ruleIndex}.allocation.dimensionValue`,
                          ).error !== undefined
                        }
                        data-intercom-target="costory-tour-vdim-dimension-value-select"
                      />
                    )}
                    {allocation.allocationType == "existingColumn" && (
                      <AxesSelector
                        {...form.getInputProps(
                          `rules.${ruleIndex}.allocation.existingColumn`,
                        )}
                        key={form.key(
                          `rules.${ruleIndex}.allocation.existingColumn`,
                        )}
                        error={
                          form.getInputProps(
                            `rules.${ruleIndex}.allocation.existingColumn`,
                          ).error !== undefined
                        }
                        data-intercom-target="costory-tour-vdim-existing-column-select"
                      />
                    )}
                  </Group>
                  <Tooltip
                    w={400}
                    multiline={true}
                    label="Helps you quickly spot unallocated costs at a glance. Use various group-by options to analyze, and effortlessly incorporate these values into your rules"
                  >
                    <Button
                      variant="outline"
                      size="sm"
                      leftSection={<IconCloudSearch />}
                      onClick={() => exploreCost()}
                      data-intercom-target="costory-tour-vdim-explore-costs-btn"
                    >
                      Explore costs
                    </Button>
                  </Tooltip>
                </Group>
                {allocation.allocationType === "splitCost" && (
                  <SplitCostResult
                    allocation={allocation}
                    prefixForm={`rules.${index}.allocation`}
                    form={form}
                    costRule={allocatedCost ?? 0}
                    vdDict={availableDimensionValues}
                  />
                )}
                {allocation.allocationType === "telemetry" && (
                  <AutoSplitTable
                    ruleCost={allocatedCost ?? 0}
                    allocation={allocation}
                    setMappingType={(value) => {
                      form.setFieldValue(`rules.${ruleIndex}.allocation`, {
                        allocationType: "telemetry",
                        datasource: allocation?.datasource,
                        mappingType: value as
                          | "mapping"
                          | "regexMapping"
                          | "identity",
                        mappingParams: {
                          ...allocation.mappingParams,
                        },
                      })
                      form.validate()
                    }}
                    availableDimensionValues={availableDimensionValues}
                    onMappingChange={({ metricName, vdimValue }) => {
                      form.setFieldValue(
                        `rules.${index}.allocation.mappingParams.mapping`,
                        {
                          ...(allocation.mappingParams.mapping || {}),
                          // do not change: we cannot use setFieldValue at metric level since metrics can contains special characters /.
                          [metricName]: vdimValue,
                        },
                      )
                      form.validate()
                    }}
                    values={allocation.mappingParams.mapping}
                    onVdimValueCreate={(value) =>
                      form.insertListItem("values", value)
                    }
                    onRegexChange={(value) => {
                      form.setFieldValue(
                        `rules.${index}.allocation.regexTransformation`,
                        value,
                      )
                      form.validate()
                    }}
                    onClose={() => {
                      form.validate()
                    }}
                    onSelectDataSource={(datasource) => {
                      form.setFieldValue(
                        `rules.${index}.allocation.datasource`,
                        datasource,
                      )
                      form.setFieldValue(
                        `rules.${index}.allocation.mappingParams.mapping`,
                        {},
                      )
                    }}
                  />
                )}
              </Stack>
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion>
      </Group>
      <Modal opened={isOpen} onClose={close} title="Rename a rule" withinPortal>
        <Group>
          <Input
            {...form.getInputProps(`rules.${ruleIndex}.name`)}
            flex={1}
            onKeyDown={(key) => (key.key == "Enter" ? close() : null)}
            data-intercom-target="costory-tour-vdim-rule-name-input"
          />
          <Button
            onClick={close}
            data-intercom-target="costory-tour-vdim-save-rule-name"
          >
            Save
          </Button>
        </Group>
      </Modal>
    </Paper>
  )
}
